<template>
  <div class="services-table-wrapper">
    <div v-if="services.length === 0" class="text-center text-muted py-5">
      <p>No services available</p>
    </div>
    <div v-else class="table-responsive">
      <table class="table table-hover services-table">
        <thead class="table-header-sticky">
          <tr>
            <th class="select-service"></th>
            <th
              v-for="column in columns"
              :key="column.column"
              class="sortable"
              @click="emit('sort', column.column)"
            >
              {{ column.label }}
              <i
                v-show="sortField === column.column"
                :class="sortOrder === 'asc' ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"
              ></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="service in services"
            :key="service.id"
            @click="selectRow(service)"
            class="service-row"
          >
            <td class="select-service" @click.stop>
              <input
                type="checkbox"
                :checked="checkoutStore.isInCart(service.id)"
                @change="toggleCartSelection(service, $event)"
                :aria-label="
                  (checkoutStore.isInCart(service.id) ? 'Remove ' : 'Add ') +
                  service.name +
                  (checkoutStore.isInCart(service.id) ? ' from request' : ' to request')
                "
              />
            </td>
            <td
              v-for="column in columns"
              :key="column.column"
              :title="getDisplayValue(service, column)"
            >
              {{ truncateText(getDisplayValue(service, column)) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCheckoutStore } from "../../stores/checkoutStore";
import { getDisplayValue } from "../../functions/serviceColumnValue";

const checkoutStore = useCheckoutStore();

const props = defineProps({
  services: {
    type: Array as () => any[],
    default: () => [],
  },
  columns: {
    type: Array as () => Array<{ label: string; column: string; type?: string }>,
    default: () => [],
  },
  sortField: {
    type: String,
    default: "",
  },
  sortOrder: {
    type: String,
    default: "asc",
  },
});

const emit = defineEmits(["row-click", "sort"]);

function selectRow(service: any) {
  emit("row-click", service);
}

function toggleCartSelection(service: any, event: Event) {
  const checked = (event.target as HTMLInputElement).checked;
  const biobank = { id: service.parentBiobankId, name: service.parentBiobankName };

  if (checked) {
    checkoutStore.addServicesToSelection(
      biobank,
      [{ label: service.name, value: service.id }],
      false
    );
  } else {
    checkoutStore.removeServicesFromSelection({ name: biobank.name }, [service.id], false);
  }
}

function truncateText(text: string, maxLength = 80): string {
  if (typeof text !== "string" || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
}
</script>

<style scoped>
.services-table-wrapper {
  width: 100%;
}

.table-responsive {
  overflow-x: auto;
}

.services-table {
  margin-bottom: 0;
  font-size: 0.95rem;
}

.table-header-sticky {
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 10;
  border-bottom: 2px solid #dee2e6;
}

.table-header-sticky th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #333;
  padding: 0.75rem;
  border: none;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #e9ecef;
}

.sortable i {
  margin-left: 0.5rem;
  font-size: 0.8rem;
  opacity: 0.5;
}

.service-row {
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

.service-row:hover {
  background-color: #f0f0f0;
}

.select-service {
  width: 2.5rem;
  text-align: center;
}

.services-table tbody td {
  color: #777;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/** The first configured column is the row's identity (e.g. Service Name by
 * default) — emphasize it regardless of which column it actually is. */
.services-table tbody td:nth-child(2) {
  font-weight: 500;
  color: inherit;
}

.text-muted {
  color: #999 !important;
}

@media (max-width: 768px) {
  .services-table {
    font-size: 0.85rem;
  }

  .table-header-sticky th {
    padding: 0.5rem 0.25rem;
  }

  .services-table tbody td {
    max-width: 140px;
  }
}
</style>
